import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Download, Database, Heart, Activity, MapPin, ShieldCheck, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const DATASETS = [
  { value: 'wellness', label: 'Wellness', icon: Heart, endpoint: '/export/wellness' },
  { value: 'load', label: 'Load / RPE', icon: Activity, endpoint: '/export/load' },
  { value: 'gps', label: 'GPS', icon: MapPin, endpoint: '/export/gps' },
  { value: 'rtp', label: 'RTP', icon: ShieldCheck, endpoint: '/export/rtp' },
  { value: 'sessions', label: 'Sessions', icon: Calendar, endpoint: '/export/sessions' },
];

const ExportPage = () => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [dataset, setDataset] = useState('wellness');
  const [teamId, setTeamId] = useState('all');
  const [athleteId, setAthleteId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (teamId && teamId !== 'all') {
      api.get(`/teams/${teamId}`).then(r => {
        setAthletes(r.data.athletes || []);
      }).catch(() => setAthletes([]));
    } else {
      setAthletes([]);
    }
    setAthleteId('all');
  }, [teamId, api]);

  const handleExport = async () => {
    const ds = DATASETS.find(d => d.value === dataset);
    if (!ds) return;

    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (teamId !== 'all') params.append('team_id', teamId);
      if (athleteId !== 'all') params.append('athlete_id', athleteId);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const qs = params.toString();
      const url = `${ds.endpoint}${qs ? '?' + qs : ''}`;
      const res = await api.get(url, { responseType: 'blob' });

      const blob = new Blob([res.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${dataset}_export.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const isDateless = dataset === 'rtp';
  const dsInfo = DATASETS.find(d => d.value === dataset);
  const DsIcon = dsInfo?.icon || Database;

  return (
    <div className="max-w-xl mx-auto" data-testid="export-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-amber-500/10 rounded-sm flex items-center justify-center border border-amber-500/20">
          <Database className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-100" data-testid="export-page-title">
            {language === 'IT' ? 'Esporta Dati' : 'Data Export'}
          </h1>
          <p className="text-sm text-zinc-500">
            {language === 'IT' ? 'Esporta dataset per analisi esterna' : 'Export datasets for external analysis'}
          </p>
        </div>
      </div>

      <div className="space-y-5 bg-zinc-900/50 border border-zinc-800 rounded-sm p-5">
        {/* Dataset */}
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">Dataset</label>
          <Select value={dataset} onValueChange={setDataset}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="dataset-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              {DATASETS.map(d => (
                <SelectItem key={d.value} value={d.value} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100">
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Team */}
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">
            {language === 'IT' ? 'Squadra' : 'Team'}
          </label>
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="team-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all" className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100">
                {language === 'IT' ? 'Tutte le squadre' : 'All Teams'}
              </SelectItem>
              {teams.map(t => (
                <SelectItem key={t.id} value={t.id} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100">
                  {t.code} — {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Athlete (optional) */}
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">
            {language === 'IT' ? 'Atleta (opzionale)' : 'Athlete (optional)'}
          </label>
          <Select value={athleteId} onValueChange={setAthleteId} disabled={!athletes.length}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100" data-testid="athlete-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all" className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100">
                {language === 'IT' ? 'Tutti gli atleti' : 'All Athletes'}
              </SelectItem>
              {athletes.map(a => (
                <SelectItem key={a.id} value={a.id} className="text-zinc-200 focus:bg-zinc-800 focus:text-zinc-100">
                  {a.first_name} {a.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date range — hidden for RTP */}
        {!isDateless && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">
                {language === 'IT' ? 'Da' : 'From'}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                data-testid="date-from"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">
                {language === 'IT' ? 'A' : 'To'}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                data-testid="date-to"
              />
            </div>
          </div>
        )}

        {/* Download */}
        <Button
          onClick={handleExport}
          disabled={downloading}
          className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-400 font-semibold h-10"
          data-testid="download-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {downloading ? (language === 'IT' ? 'Scaricamento...' : 'Downloading...') : (language === 'IT' ? 'Scarica CSV' : 'Download CSV')}
        </Button>
      </div>

      {/* Info card */}
      <div className="mt-4 p-3 bg-zinc-950/50 border border-zinc-800/50 rounded-sm">
        <div className="flex items-center gap-2 mb-1">
          <DsIcon className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-xs font-medium text-zinc-400">{dsInfo?.label}</span>
        </div>
        <p className="text-[11px] text-zinc-600">
          {dataset === 'wellness' && 'Daily wellness scores: fatigue, sleep, soreness, stress, mood, readiness.'}
          {dataset === 'load' && 'Load/RPE data from GPS events: duration, RPE, calculated training load.'}
          {dataset === 'gps' && 'GPS metrics: total distance, HSR, sprint distance, accelerations, decelerations.'}
          {dataset === 'rtp' && 'Return-to-Play profiles: injury, phase, status, sessions completed.'}
          {dataset === 'sessions' && 'Session history with assignments: type, title, assigned athletes, status.'}
        </p>
      </div>
    </div>
  );
};

export default ExportPage;
